import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Progress, Spin, Tooltip } from '@arco-design/web-react';
import { IconExclamationCircle, IconQuestionCircle, IconToBottom, IconToTop } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';
import { get } from 'lodash';

import { Indicator } from '@/components/Indicator/Indicator';

import { isDetailDescriptionField, renderDetailTextValue } from '../DetailText';
import styles from './index.module.less';

interface StatusProps {
  status?: any;
  label?: string;
  msg?: string;
  tip?: string;
  percent?: number;
}

// 数据库默认时间
const defaultTimes = [
  /^0001-01-01T00:00:00/, // .NET MinValue
  /^0000-00-00/, // 一些数据库的空日期
  /^0001-01-/, // 一些数据库的空日期
  /^1970-01-01T00:00:00/, // Unix Epoch (有时用作默认)
  /^1900-01-01/, // SQL Server旧版本默认
  /^9999-12-31/, // 最大日期（有时用作占位符）
];
export const dateFormatRender = (text) =>
  text && defaultTimes.every((pattern) => !pattern.test(text)) ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-';

export const defaultRender = (text, record: any) => text || '-';

/** 描述字段统一排在详情基础信息末尾。 */
function orderDetailFields(fields: Record<string, unknown> | unknown[]) {
  return Object.entries(fields).sort(([leftKey, leftValue]: any[], [rightKey, rightValue]: any[]) => {
    const leftIsDescription = isDetailDescriptionField(String(leftValue?.key || leftKey));
    const rightIsDescription = isDetailDescriptionField(String(rightValue?.key || rightKey));
    return Number(leftIsDescription) - Number(rightIsDescription);
  });
}

export function RenderStatus({ status, label, msg, tip, percent }: StatusProps) {
  const { t } = useTranslation();
  const arr = ['success', 'successed', 'running', 'up'];

  const _status = useMemo(() => {
    return status ? status?.toLowerCase() : 'unknown';
  }, [status]);

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, whiteSpace: 'nowrap' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <Indicator status={_status} />
        <span style={{ marginLeft: 4 }}>
          {label || t('common:unknown')} {percent !== undefined && !Number.isNaN(percent) && `(${percent}%)`}
        </span>
        {msg && arr.indexOf(_status) === -1 && (
          <Tooltip content={msg} position="bottom">
            <IconExclamationCircle style={{ marginLeft: 4, marginTop: 2 }} />
          </Tooltip>
        )}
        {tip && (
          <Tooltip content={tip} position="bottom">
            <IconQuestionCircle style={{ marginLeft: 4, marginTop: -1 }} />
          </Tooltip>
        )}
      </span>

      {percent !== undefined && !Number.isNaN(percent) && (
        <Progress percent={percent} showText={false} style={{ width: '100px', marginLeft: '16px' }} />
      )}
    </div>
  );
}

export function RenderFields({
  fields,
  data,
  name,
  nameLabel,
  statusList,
  loading,
  contentStyle,
  icon,
  actions,
  actionNode,
  showToggle = true,
}: {
  fields: any;
  data: any;
  name?: string;
  nameLabel?: string;
  statusList?: StatusProps[];
  loading?: boolean;
  contentStyle?: any;
  icon?: React.ReactNode | null | undefined;
  actions?: React.ReactNode[] | null | undefined;
  actionNode?: React.ReactNode | null | undefined;
  /** 是否展示基本信息区域的置顶/展开按钮，默认展示。 */
  showToggle?: boolean;
}) {
  const { t } = useTranslation();
  const [top, setTop] = useState(false);

  const topStyle = useMemo(() => {
    if (!top) return {};
    return {
      height: 0,
      overflow: 'hidden',
      padding: 0,
    };
  }, [top]);

  return (
    <div className={styles.FieldsContainer} style={top || !showToggle ? {} : { paddingBottom: 24 }}>
      {(name || actions?.length > 0 || actionNode) && (
        <div className={styles.flex_space}>
          <div className={styles.flex_space_t}>
            <span className={styles.fields_title}>
              {nameLabel || t('common:name')}: {name || data?.name}
              {statusList?.length > 0 && (
                <span className={styles.fields_subtitle}>
                  {statusList?.map((item) => <RenderStatus {...item} key={`${item?.label}-${item?.status}`} />)}
                </span>
              )}
              {icon || null}
            </span>
          </div>
          {actions?.length > 0 && (
            <div className={styles.gap10}>
              {actions?.map((item: any, index) => {
                const { label, ...rest } = item;
                return (
                  <Button {...rest} key={index}>
                    {label}
                  </Button>
                );
              })}
            </div>
          )}
          {actionNode || null}
        </div>
      )}
      <div className={styles.fields_card} style={topStyle}>
        <Spin loading={loading} style={{ width: '100%' }}>
          <div className={styles.fields_content} style={contentStyle}>
            {orderDetailFields(fields).map(([k, v]: any[], index) => {
              let value = get(data, k);
              const fieldKey = String(v?.key || k);

              // 字符串和对象配置共用字段布局，为单行省略提供可收缩的内容区域。
              const style = v?.style || {};
              const label = (typeof v === 'string' ? v : v?.label || v?.name || '').trim();

              // 如果有自定义渲染函数，使用它处理 value
              if (typeof v?.render === 'function') {
                value = v.render(value, data);
              }

              const displayValue = renderDetailTextValue(fieldKey, value || v?.value || '-');
              const fieldStyle = {
                ...(v?.span ? { gridColumn: `span ${v.span}` } : {}),
                ...(isDetailDescriptionField(fieldKey) ? { gridColumn: '1 / -1', alignItems: 'flex-start' } : {}),
                ...style,
              };
              const valueWrapStyle = v?.ellipsisSingleLine
                ? { minWidth: 0, flex: 1, overflow: 'hidden' }
                : { minWidth: 0, flex: 1, wordBreak: 'break-all' as const };

              return (
                <span key={`${label}-${value}-${index}`} style={{ display: 'flex', alignItems: 'center', ...fieldStyle, minWidth: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>{label}：</span>
                  <span style={valueWrapStyle}>{displayValue}</span>
                </span>
              );
            })}
          </div>
        </Spin>
      </div>
      {showToggle && (
        <span className={styles.openIcon} onClick={() => setTop((pre) => !pre)}>
          {top ? <IconToBottom /> : <IconToTop />}
          <span style={{ marginLeft: 4 }}>{top ? t('components:collapse') : t('components:pin.to.top')}</span>
        </span>
      )}
    </div>
  );
}
